import { Divider, Heading, IconArrowBigLeft, IconLink, Text } from "@idg-web-platform/pulse";
import css from "./inStoreOrderDetailsPage.module.css";
import { formatAmount, formatDate } from "../utils/formats.ts";
import { NavigationWrapper } from "../components/navigation/navigationWrapper.tsx";
import { useEffect } from "preact/hooks";
import { analyticsContentViewed } from "../utils/analyticsContentViewed.ts";

InStoreOrderDetailsPage.tagName = "control-in-store-order-details-page";

interface InStoreLineItemDisplay {
  articleId: string;
  articleName: string;
  brandName?: string;
  quantity: string;
  price: number;
  sumPrice: number;
  originalPrice?: number; // UVP (recommended retail price)
  color?: string;
  size?: string;
}

interface InStoreOrderDetailsProps {
  order: {
    id: string;
    date: Date;
    total: number;
    storeName?: string;
    storeAddress?: string;
    lineItems: InStoreLineItemDisplay[];
  };
}

function renderInStoreLineItem(item: InStoreLineItemDisplay) {
  const hasDiscount = item.originalPrice && item.originalPrice > item.price;
  const quantity = Number(item.quantity) || 1;
  const displaySumPrice = quantity > 1 ? item.sumPrice : item.price;

  return (
    <div className={css.lineItem} key={item.articleId}>
      <div className={css.lineItemContent}>
        <div className={css.lineItemMain}>
          {item.brandName && <div className={css.brandName}>{item.brandName}</div>}
          <div className={css.articleName}>{item.articleName}</div>

          <div className={css.attributes}>
            {item.color && (
              <div className={css.attribute}>
                <span className={css.attributeLabel}>Farbe:</span>
                <span className={css.attributeValue}>{item.color}</span>
              </div>
            )}
            {item.size && (
              <div className={css.attribute}>
                <span className={css.attributeLabel}>Größe:</span>
                <span className={css.attributeValue}>{item.size}</span>
              </div>
            )}
            {quantity > 1 && (
              <div className={css.attribute}>
                <span className={css.attributeLabel}>Menge:</span>
                <span className={css.attributeValue}>{item.quantity}</span>
                <span className={css.attributeLabel}>Einzelpreis:</span>
                <span className={css.attributeValue}>{formatAmount(item.price)}</span>
              </div>
            )}
          </div>

          <div className={css.articleNumber}>
            <span className={css.attributeLabel}>Artikelnr:</span>
            <span className={css.attributeValue}>{item.articleId}</span>
          </div>
        </div>

        <div className={css.lineItemPrice}>
          <Text
            sizeStep={0}
            fontStyle="bold"
            removeMargin={true}
            class={hasDiscount ? css.discountedPrice : undefined}
          >
            {formatAmount(displaySumPrice)}
          </Text>
          {hasDiscount && (
            <Text sizeStep={-1} removeMargin={true} class={css.originalPrice}>
              UVP {formatAmount(item.originalPrice!)}
            </Text>
          )}
        </div>
      </div>
    </div>
  );
}

export function InStoreOrderDetailsPage({ order }: InStoreOrderDetailsProps) {
  useEffect(() => {
    analyticsContentViewed("in_store_order_details");
  }, []);

  const storeInfo = order.storeName
    ? `${order.storeName}${order.storeAddress ? `, ${order.storeAddress}` : ""}`
    : undefined;

  return (
    <>
      <NavigationWrapper currentPath="/c/orders">
        <div className={css.root}>
          <div className={css.backLink}>
            <IconLink
              text="Bestellübersicht"
              href="/c/orders"
              iconComponent={<IconArrowBigLeft />}
              iconPosition="left"
            />
          </div>

          <div className={css.header}>
            <Heading
              class={css.headerHeadline}
              sizeStep={2}
              removeMargin={true}
              element="h1"
              text={`Einkauf im Store vom ${formatDate(order.date)}`}
            />
            {storeInfo && (
              <div className={css.storeInfo}>
                <span className={css.storeLabel}>Store:</span>
                <span className={css.storeValue}>{storeInfo}</span>
              </div>
            )}
          </div>

          <Divider level="primary" className={css.divider} />

          <div className={css.lineItems}>
            {order.lineItems.map((item) => renderInStoreLineItem(item))}
          </div>

          <Divider level="primary" className={css.divider} />

          <div className={css.totalSection}>
            <Text sizeStep={0} removeMargin={true}>
              Gesamt{" "}
              <Text element="span" sizeStep={-1} removeMargin={true}>
                (inkl. Mwst.)
              </Text>
            </Text>
            <Text sizeStep={1} fontStyle="bold" removeMargin={true}>
              {formatAmount(order.total)}
            </Text>
          </div>
        </div>
      </NavigationWrapper>
    </>
  );
}
